// src/components/UserMaintenance/UserMaintenance.js
import Icon from '../common/Icon';
import { useState, useRef, useEffect, useMemo } from 'react';
import AgGrid from '../common/AgGrid';
import CommonModal from '../common/CommonModal';
import { Eye, EyeOff, Edit } from 'lucide-react';
import SuccessMessage from '../common/SuccessMessage';
import {
    GetUserDetails,
    CreateUser,
    GetUserById,
    GetUserType,
    GetManagedClientsUserModule,
    UpdateUserById,
} from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader';


const UserMaintenance = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
 
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successText, setSuccessText] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        username: '',
        userType: '',
        active: false,
        adminAccess: false,
        managedClients: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [assignedClients, setAssignedClients] = useState([]);
    const [modalMode, setModalMode] = useState('add');
    const [editingUserId, setEditingUserId] = useState(null);
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    const [searchText, setSearchText] = useState('');


    const [userTypes, setUserTypes] = useState([]);
    const [loadingUserTypes, setLoadingUserTypes] = useState(false);

 
    const [managedClientTypes, setManagedClientTypes] = useState([]);
    const [loadingClientTypes, setLoadingClientTypes] = useState(false);

    const gridRef = useRef(null);

   
    const toPascalCase = (str) => {
        if (!str || typeof str !== 'string') return '';

        return str
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

   
    useEffect(() => {
        fetchUsers();
        fetchUserTypes();
        fetchManagedClientTypes();
    }, []);


    const fetchManagedClientTypes = async () => {
        try {
            setLoadingClientTypes(true);
            const response = await GetManagedClientsUserModule();

            let clientTypesData = [];

            if (response && response.success === true && Array.isArray(response.data)) {
                clientTypesData = response.data;
            } else if (Array.isArray(response)) {
                clientTypesData = response;
            } else {
                console.warn('Unexpected API response structure:', response);
            }

            if (clientTypesData && Array.isArray(clientTypesData) && clientTypesData.length > 0) {
                const mappedClientTypes = clientTypesData.map(type => {
                    const clientTypeID = type.id || type.clientTypeID || '';
                    const clientTypeName = type.name || type.clientTypeName || 'Unnamed Type';

                    return {
                        clientTypeID: clientTypeID,
                        clientTypeName: clientTypeName,
                        id: clientTypeID.toString(),
                        value: clientTypeID.toString(),
                        label: clientTypeName
                    };
                });

                mappedClientTypes.sort((a, b) => a.label.localeCompare(b.label));
                setManagedClientTypes(mappedClientTypes);
                return mappedClientTypes;
            } else {
                setManagedClientTypes([]);
                return [];
            }
        } catch (err) {
            console.error("Error fetching managed client types:", err);
            setManagedClientTypes([]);
            return [];
        } finally {
            setLoadingClientTypes(false);
        }
    };

  
    const fetchUserTypes = async () => {
        try {
            setLoadingUserTypes(true);
            const types = await GetUserType();

            if (types && Array.isArray(types)) {
                setUserTypes(types);
            } else {
                setUserTypes([]);
            }
        } catch (err) {
            console.error("Error fetching user types:", err);
            setUserTypes([]);
        } finally {
            setLoadingUserTypes(false);
        }
    };



  
    const mapUserTypeFromApi = (apiUserType) => {
        if (!apiUserType) return '';

        if (userTypes.length > 0) {
            const foundType = userTypes.find(type =>
                type.userTypeName === apiUserType ||
                type.userTypeName?.toLowerCase() === apiUserType.toLowerCase() ||
                type.id === apiUserType ||
                type.value === apiUserType
            );

            if (foundType) {
                return foundType.userTypeName || foundType.value || foundType.id;
            }
        }

        const typeMap = {
            'Employee': 'employee',
            'Admin': 'admin',
            'Cager': 'cager',
            'Vendor': 'vendor',
            'Consultant': 'consultant',
            'Client': 'client'
        };

        const lowerType = apiUserType.toLowerCase();
        for (const [key, value] of Object.entries(typeMap)) {
            if (key.toLowerCase() === lowerType) {
                return value;
            }
        }

        return apiUserType;
    };

 
    const mapUserTypeToApi = (dropdownValue) => {
        if (userTypes.length > 0) {
            const foundType = userTypes.find(type =>
                type.userTypeName === dropdownValue ||
                type.userTypeName?.toLowerCase() === dropdownValue.toLowerCase() ||
                type.id === dropdownValue ||
                type.value === dropdownValue
            );

            if (foundType) {
                return foundType.userTypeName || foundType.value || foundType.id;
            }
        }

        const typeMap = {
            'employee': 'Employee',
            'admin': 'Admin',
            'cager': 'Cager',
            'vendor': 'Vendor',
            'consultant': 'Consultant',
            'client': 'Client'
        };

        return typeMap[dropdownValue] || dropdownValue.charAt(0).toUpperCase() + dropdownValue.slice(1);
    };

   
    const getUserTypeIdFromName = (userTypeName) => {
        const userTypeMap = {
            'Admin': 7,
            'Cager': 1,
            'Client': 2,
            'Consultant': 3,
            'Employee': 4,
            'List Agent': 5,
            'Vendor': 6
        };

        return userTypeMap[userTypeName];
    };

  
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const users = await GetUserDetails();

            if (users && Array.isArray(users)) {
                const transformedUsers = users.map(user => ({
                    id: user.userID || user.id || `USR${Math.floor(Math.random() * 1000)}`,
                    fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.fullName || 'N/A',
                    userName: user.userName || user.username || 'N/A',
                    userType: user.userTypeName || user.userType || user.role || 'Employee',
                    isActive: user.isActive,
                    edit: true,
                    originalData: user,
                    managedClientIds: user.managedClientIds || [],
                    managedClientNames: user.managedClientNames || []
                }));

                setRowData(transformedUsers);
            } else {
                setRowData([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
          
            setRowData([]);
        } finally {
            setLoading(false);
        }
    };

 
    const handleEditClick = async (event, userId) => {
        event.stopPropagation();
        const user = rowData.find(u => u.id === userId);
        const originalData = user?.originalData || {};

        if (!user) {
            console.error('User not found');
            return;
        }

        setEditLoading(true);

        try {
            const userId = originalData.userID || user.id;

            if (!userId) {
                throw new Error('User ID not found');
            }

            const userDetails = await GetUserById(userId);

            if (userDetails) {
                setFormData({
                    firstName: userDetails.firstName || '',
                    lastName: userDetails.lastName || '',
                    email: userDetails.email || originalData.email || '',
                    password: '',
                    username: userDetails.userName || originalData.userName || user.userName || '',
                    userType: mapUserTypeFromApi(userDetails.userTypeName || userDetails.userType || originalData.userTypeName),
                    active: userDetails.isActive !== undefined ? userDetails.isActive : (originalData.isActive !== undefined ? originalData.isActive : true),
                    adminAccess: userDetails.isAdmin || originalData.isAdmin || false,
                    managedClients: ''
                });

                const assignedClientsArray = [];

                if (userDetails.managedClientIds && Array.isArray(userDetails.managedClientIds) &&
                    userDetails.managedClientNames && Array.isArray(userDetails.managedClientNames)) {
                    for (let i = 0; i < userDetails.managedClientIds.length; i++) {
                        const clientId = userDetails.managedClientIds[i];
                        const clientName = userDetails.managedClientNames[i] || '';

                        assignedClientsArray.push({
                            clientName: clientName,
                            clientID: clientId?.toString() || '',
                            id: clientId?.toString() || '',
                            clientTypeID: clientId,
                            clientTypeName: clientName
                        });
                    }
                }

                if (userDetails.managedClients && Array.isArray(userDetails.managedClients)) {
                    userDetails.managedClients.forEach(client => {
                        const existingClient = assignedClientsArray.find(c =>
                            c.clientID === (client.id?.toString() || client.clientTypeID?.toString())
                        );

                        if (!existingClient) {
                            assignedClientsArray.push({
                                clientName: client.clientTypeName || client.clientName || client.name || '',
                                clientID: client.clientTypeID?.toString() || client.id?.toString() || '',
                                id: client.clientTypeID?.toString() || client.id?.toString() || '',
                                clientTypeID: client.clientTypeID || client.id,
                                clientTypeName: client.clientTypeName || client.clientName || client.name
                            });
                        }
                    });
                }

                setAssignedClients(assignedClientsArray);
            } else {
                throw new Error('No user details returned from API');
            }

        } catch (error) {
            console.error('Error fetching user details:', error);
            const nameParts = user.fullName.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            setFormData({
                firstName: firstName,
                lastName: lastName,
                email: originalData.email || `${user.userName.toLowerCase()}@company.com`,
                password: '',
                username: originalData.userName || user.userName,
                userType: mapUserTypeFromApi(originalData.userTypeName || user.userType),
                active: originalData.isActive !== undefined ? originalData.isActive : true,
                adminAccess: originalData.isAdmin || false,
                managedClients: ''
            });

            const assignedClientsArray = [];

            if (originalData.managedClientIds && Array.isArray(originalData.managedClientIds) &&
                originalData.managedClientNames && Array.isArray(originalData.managedClientNames)) {
                for (let i = 0; i < originalData.managedClientIds.length; i++) {
                    const clientId = originalData.managedClientIds[i];
                    const clientName = originalData.managedClientNames[i] || '';

                    assignedClientsArray.push({
                        clientName: clientName,
                        clientID: clientId?.toString() || '',
                        id: clientId?.toString() || '',
                        clientTypeID: clientId,
                        clientTypeName: clientName
                    });
                }
            }

            if (user.managedClientIds && Array.isArray(user.managedClientIds) &&
                user.managedClientNames && Array.isArray(user.managedClientNames)) {
                for (let i = 0; i < user.managedClientIds.length; i++) {
                    const clientId = user.managedClientIds[i];
                    const clientName = user.managedClientNames[i] || '';

                    const existingClient = assignedClientsArray.find(c =>
                        c.clientID === clientId?.toString()
                    );

                    if (!existingClient) {
                        assignedClientsArray.push({
                            clientName: clientName,
                            clientID: clientId?.toString() || '',
                            id: clientId?.toString() || '',
                            clientTypeID: clientId,
                            clientTypeName: clientName
                        });
                    }
                }
            }

            setAssignedClients(assignedClientsArray);
        } finally {
            setEditLoading(false);
            setModalMode('edit');
            setEditingUserId(originalData.userID || user.id);
            setIsModalOpen(true);
        }
    };

    // Edit Icon Component with Tooltip
    const EditIconRenderer = (params) => {
        const user = params.data;
        const [showTooltip, setShowTooltip] = useState(false);

        return (
            <div className='edit-icon-render'>
                <div
                    onClick={(e) => handleEditClick(e, user.id)}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        padding: '4px',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s',
                        backgroundColor: showTooltip ? '#f0f0f0' : 'transparent'
                    }}
                    title="Edit"
                >
                    <Edit size={16} color="#0D6EFD" />

                    {/* Tooltip */}
                    {showTooltip && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#333',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '3px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            marginTop: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            pointerEvents: 'none'
                        }}>
                            Edit
                        </div>
                    )}
                </div>
            </div>
        );
    };

 
    const ToggleSwitch = ({ field, formData, onChange }) => {
        const handleToggle = (fieldName) => {
            if (onChange) {
                onChange({
                    target: {
                        name: fieldName,
                        type: 'checkbox',
                        checked: !formData[fieldName]
                    }
                });
            }
        };

     
        const isEmployee = (formData?.userType || '').toLowerCase() === 'employee';

        return (
            <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                <div style={{ flex: 1 }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333'
                    }}>
                        Active
                    </label>
                    <div
                        onClick={() => handleToggle('active')}
                        style={{
                            width: '50px',
                            height: '26px',
                            backgroundColor: formData['active'] ? '#0D6EFD' : '#ccc',
                            borderRadius: '13px',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s'
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '3px',
                            left: formData['active'] ? '27px' : '3px',
                            width: '20px',
                            height: '20px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            transition: 'left 0.3s'
                        }} />
                    </div>
                </div>


                {isEmployee && (
                    <div style={{ flex: 1 }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#333'
                        }}>
                            Admin Access
                        </label>
                        <div
                            onClick={() => {
                             
                                if (isEmployee) return;
                                handleToggle('adminAccess');
                            }}
                            style={{
                                width: '50px',
                                height: '26px',
                             
                                backgroundColor: (isEmployee || formData['adminAccess']) ? '#0D6EFD' : '#ccc',
                                borderRadius: '13px',
                                position: 'relative',
                                cursor: isEmployee ? 'not-allowed' : 'pointer',
                                opacity: isEmployee ? 0.85 : 1,
                                transition: 'background-color 0.3s'
                            }}
                            title={isEmployee ? 'Admin Access is always enabled for Employee users' : ''}
                        >
                            <div style={{
                                position: 'absolute',
                                top: '3px',
                           
                                left: (isEmployee || formData['adminAccess']) ? '27px' : '3px',
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                transition: 'left 0.3s'
                            }} />
                        </div>
                    </div>
                )}
            </div>
        );
    };

  
    const ManagedClientsWithAddButton = ({ field, formData, onChange, error, touched, onCustomChange, modalBodyRef }) => {
        const [dropdownDirection, setDropdownDirection] = useState('down');
        const [selectAll, setSelectAll] = useState(false);
        const [dropdownOpen, setDropdownOpen] = useState(false);
        const [selectedDropdownClientIds, setSelectedDropdownClientIds] = useState(new Set());
        const [showConfirmModal, setShowConfirmModal] = useState(false);
        const [clientToRemove, setClientToRemove] = useState(null);
        const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);
        const [searchQuery, setSearchQuery] = useState(''); 
        const dropdownRef = useRef(null);
        const buttonRef = useRef(null);

      
        const { required = false } = field || {};

     
        const isClientType = (formData?.userType || '').toLowerCase() === 'client';

       
        const filteredClientTypes = searchQuery.trim() === ''
            ? managedClientTypes
            : managedClientTypes.filter(clientType => {
                const clientName = (clientType.clientTypeName || clientType.label || '').toLowerCase();
                const search = searchQuery.toLowerCase();
                return clientName.includes(search);
            });

        // Close dropdown when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setDropdownOpen(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

    
        useEffect(() => {
            if (!dropdownOpen) {
                setSearchQuery('');
            }
        }, [dropdownOpen]);

        useEffect(() => {
            if (onCustomChange) {
                const value = assignedClients.length > 0
                    ? assignedClients.map(c => c.clientID).join(',')
                    : '';
                onCustomChange('managedClients', value);
            }
          
        }, [assignedClients]);

      
        const getSelectedDisplayText = () => {
            if (selectedDropdownClientIds.size === 0) {
                return required ? 'Select at least one client ' : 'Select a client to assign';
            }

            const selectedNames = [];
            selectedDropdownClientIds.forEach(clientId => {
                const clientType = managedClientTypes.find(type =>
                    type.clientTypeID?.toString() === clientId ||
                    type.id === clientId ||
                    type.value === clientId
                );
                if (clientType) {
                    selectedNames.push(clientType.clientTypeName || `Client Type ${clientId}`);
                }
            });

            if (selectedNames.length <= 2) {
                return selectedNames.join(', ');
            }
            return `${selectedNames.length} clients selected`;
        };

    
        const handleAddMultipleClients = (e) => {
            e.preventDefault();

            if (selectedDropdownClientIds.size === 0) {
                alert('Please select at least one client type to add.');
                return;
            }

            const clientsToAdd = [];

            selectedDropdownClientIds.forEach(clientId => {
                const selectedClientType = managedClientTypes.find(type =>
                    type.clientTypeID?.toString() === clientId ||
                    type.id === clientId ||
                    type.value === clientId
                );

                if (selectedClientType) {
                    const isAlreadyAssigned = assignedClients.some(client =>
                        client.clientID === selectedClientType.clientTypeID?.toString() ||
                        client.clientID === selectedClientType.id ||
                        client.clientID === selectedClientType.value ||
                        client.clientTypeID?.toString() === selectedClientType.clientTypeID?.toString()
                    );

                    if (!isAlreadyAssigned) {
                        clientsToAdd.push({
                            clientName: selectedClientType.clientTypeName || `Client Type ${selectedClientType.clientTypeID}`,
                            clientID: selectedClientType.clientTypeID?.toString() || selectedClientType.id || selectedClientType.value || clientId,
                            id: selectedClientType.clientTypeID?.toString() || selectedClientType.id || selectedClientType.value || clientId,
                            clientTypeID: selectedClientType.clientTypeID,
                            clientTypeName: selectedClientType.clientTypeName
                        });
                    }
                }
            });

            if (clientsToAdd.length > 0) {
                if (isClientType) {
                    // ✅ NEW: Client user type — hamesha sirf 1 client (naya select purane ko replace karega)
                    setAssignedClients(clientsToAdd.slice(0, 1));
                } else {
                    setAssignedClients([...assignedClients, ...clientsToAdd]);
                }
                setSelectedDropdownClientIds(new Set());
                setSelectAll(false);
                setDropdownOpen(false);

              
                if (onCustomChange) {
                    onCustomChange('managedClients', Array.from(selectedDropdownClientIds).join(','));
                }
            } else {
                alert('Client(s) already assigned.');
            }
        };

      
        const handleRemoveClient = (clientId) => {
            const updatedClients = assignedClients.filter(client => client.clientID !== clientId);
            setAssignedClients(updatedClients);

         
            if (required && updatedClients.length === 0) {
                if (onCustomChange) {
                    onCustomChange('managedClients', '');
                }
            }
        };

       
        const handleDropdownCheckboxChange = (clientId, isChecked) => {
        
            if (isClientType) {
                setSelectedDropdownClientIds(isChecked ? new Set([clientId]) : new Set());
                setSelectAll(false);
                return;
            }

            const newSelectedIds = new Set(selectedDropdownClientIds);

            if (isChecked) {
                newSelectedIds.add(clientId);
            } else {
                newSelectedIds.delete(clientId);
                setSelectAll(false);
            }

            setSelectedDropdownClientIds(newSelectedIds);
        };

    
        const handleSelectAllChange = (isChecked) => {
           
            if (isClientType) return;

            setSelectAll(isChecked);

            if (isChecked) {
                const allClientIds = new Set();
                managedClientTypes.forEach(type => {
                    const clientId = type.clientTypeID?.toString() || type.id || type.value;
                    if (clientId) {
                        allClientIds.add(clientId);
                    }
                });
                setSelectedDropdownClientIds(allClientIds);
            } else {
                setSelectedDropdownClientIds(new Set());
            }
        };

        const isAddButtonDisabled = selectedDropdownClientIds.size === 0 || loadingClientTypes;

       
        const GRID_ROW_HEIGHT = 50;
        const GRID_HEADER_HEIGHT = 50;
        const GRID_MAX_HEIGHT = 440;
        const computedGridHeight = Math.min(
            GRID_MAX_HEIGHT,
            GRID_HEADER_HEIGHT + (assignedClients.length * GRID_ROW_HEIGHT) + 8
        );

    
        const handleAddClient = (e) => {
            e.preventDefault();
            if (selectedDropdownClientIds.size > 0) {
                handleAddMultipleClients(e);
            } else {
                alert('Please select at least one client type from the dropdown.');
            }
        };

      
        const handleConfirmRemove = () => {
            if (clientToRemove) {
                handleRemoveClient(clientToRemove.clientID);
            }
            setShowConfirmModal(false);
            setClientToRemove(null);
        };

        // Handle cancel from modal
        const handleCancelRemove = () => {
            setShowConfirmModal(false);
            setClientToRemove(null);
        };

       
        const handleRemoveAllClick = () => {
            if (assignedClients.length === 0) return;
            setShowRemoveAllConfirm(true);
        };

        const handleConfirmRemoveAll = () => {
            setAssignedClients([]);
            setShowRemoveAllConfirm(false);
        };

        const handleCancelRemoveAll = () => {
            setShowRemoveAllConfirm(false);
        };

    
        const RemoveIconRenderer = (params) => {
            const handleRemoveClick = () => {
                setClientToRemove(params.data);
                setShowConfirmModal(true);
            };

            return (
                <div className='remove-button-container'>
                    <button
                        onClick={handleRemoveClick}
                        className='remove-btn'
                        type="button"
                        style={{
                            padding: '4px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#c82333';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#dc3545';
                        }}
                    >
                        Remove
                    </button>
                </div>
            );
        };

        const CustomTooltip = (props) => {
            const { value } = props;

            if (!value) return null;

            return (
                <div
                    style={{
                        backgroundColor: '#333',
                        color: '#fff',
                        padding: '8px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        maxWidth: '300px',
                        whiteSpace: 'normal',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                >
                    {value}
                </div>
            );
        };

        const tooltipCellStyle = {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: 'pointer'


        };

      
        const assignedClientsColumnDefs = [
            {
                field: 'clientName',
                headerName: 'Client Name',
                minWidth: 200,
                tooltipField: 'clientName',
                tooltipComponent: 'customTooltip',
                cellStyle: tooltipCellStyle
            },

            {
                field: 'clientID',
                headerName: 'Client ID',
                filter: false,
                sortable: false,
                width: 100
            },
            {
                field: 'remove',
                headerName: 'Remove',
                filter: false,
                sortable: false,
                width: 100,
                cellRenderer: RemoveIconRenderer,
                cellStyle: {
                    textAlign: 'center',
                    padding: '4px 0'
                }
            }
        ];

      
        const getDropdownBorderStyle = () => {
            if (error && touched) {
                return '1px solid #dc3545';
            }
            return '1px solid #ced4da';
        };

        const DROPDOWN_MAX_HEIGHT = 200;

        const checkDropdownDirection = () => {
            if (!buttonRef.current || !modalBodyRef?.current) return;

            const buttonRect = buttonRef.current.getBoundingClientRect();
            const modalRect = modalBodyRef.current.getBoundingClientRect();

            const spaceBelow = modalRect.bottom - buttonRect.bottom;

           

            if (spaceBelow < DROPDOWN_MAX_HEIGHT) {
                setDropdownDirection('up');
            } else {
                setDropdownDirection('down');
            }
        };

        useEffect(() => {
            const handleResize = () => {
                if (dropdownOpen) checkDropdownDirection();
            };

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, [dropdownOpen]);

        return (
            <>
                <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '16px' }}>
                    <label className="form-label">
                        Managed Client
                        {/* <span style={{ color: 'red', marginLeft: '4px' }}>       *    </span> */}
                        {required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                    </label>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <div style={{ flex: 0.8, width: '60%' }} ref={dropdownRef}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    ref={buttonRef}
                                    type="button"

                                    onClick={() => {
                                        if (!dropdownOpen) {
                                            checkDropdownDirection();
                                        }
                                        setDropdownOpen(prev => !prev);
                                    }}


                                    className='user-type-dropdown'
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '8px 12px',
                                        border: getDropdownBorderStyle(),
                                        borderRadius: '4px',
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        outline: 'none'
                                    }}
                                    disabled={loadingClientTypes}
                                >
                                    <span style={{
                                        color: selectedDropdownClientIds.size > 0 ? '#333' : required && error && touched ? '' : '#6c757d',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {loadingClientTypes ? 'Loading client types...' : getSelectedDisplayText()}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            color: '#6c757d',
                                            transform: dropdownOpen && dropdownDirection === 'up'
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease'
                                        }}
                                    >
                                        ▼
                                    </span>
                                </button>

                                {/* ✅ NEW: DROPDOWN WITH SEARCH FUNCTIONALITY */}
                                {dropdownOpen && managedClientTypes.length > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        maxHeight: '300px', // ✅ INCREASED from 200px
                                        overflowY: 'auto',
                                        top: dropdownDirection === 'down' ? '100%' : 'auto',
                                        bottom: dropdownDirection === 'up' ? '100%' : 'auto',
                                        marginTop: dropdownDirection === 'down' ? '2px' : '0',
                                        marginBottom: dropdownDirection === 'up' ? '2px' : '0',
                                    }}>
                                        {/* ✅ NEW: SEARCH INPUT */}
                                        <div style={{
                                            padding: '8px 12px',
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: '#f8f9fa',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1001
                                        }}>
                                            <input
                                                type="text"
                                                placeholder="Search clients..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                style={{
                                                    width: '100%',
                                                    padding: '6px 8px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '3px',
                                                    fontSize: '13px',
                                                    outline: 'none',
                                                    fontFamily: 'inherit'
                                                }}
                                            />
                                        </div>

                                        {/* SELECT ALL CHECKBOX — ✅ Client user type ke liye hidden (sirf 1 client allowed) */}
                                        {filteredClientTypes.length > 0 && (
                                            <div style={{
                                                padding: '8px 12px',
                                                borderBottom: '1px solid #eee',
                                                backgroundColor: '#f8f9fa',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                position: 'sticky',
                                                top: '48px', 
                                                zIndex: 1000
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>(All)</span>
                                            </div>
                                        )}

                                      
                                        {filteredClientTypes.length > 0 ? (
                                            filteredClientTypes.map((clientType) => {
                                                const clientId = clientType.clientTypeID?.toString() || clientType.id || clientType.value;
                                                const clientName = clientType.clientTypeName || `Client Type ${clientType.clientTypeID}`;
                                                const isChecked = selectedDropdownClientIds.has(clientId);

                                                return (
                                                    <div
                                                        key={clientId}
                                                        style={{
                                                            padding: '8px 12px',
                                                            borderBottom: '1px solid #f5f5f5',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            cursor: 'pointer',
                                                            backgroundColor: isChecked ? '#f0f8ff' : 'white',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownCheckboxChange(clientId, !isChecked);
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleDropdownCheckboxChange(clientId, e.target.checked);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <span style={{
                                                            fontSize: '14px',
                                                            minWidth: "300px",
                                                            color: isChecked ? '#0D6EFD' : '#333'
                                                        }}>
                                                            {clientName}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                           
                                            <div style={{
                                                padding: '12px',
                                                textAlign: 'center',
                                                color: '#6c757d',
                                                fontSize: '13px',
                                            }}>
                                                No clients found matching "{searchQuery}"
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {selectedDropdownClientIds.size > 0 && (
                                <div style={{
                                    marginTop: '4px',
                                    fontSize: '12px',
                                    color: '#0D6EFD',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <span>{selectedDropdownClientIds.size} selected</span>
                                    <button
                                        onClick={() => {
                                            setSelectedDropdownClientIds(new Set());
                                            setSelectAll(false);
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            padding: 0
                                        }}
                                        type="button"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>

                        <div style={{ flex: 0.25, width: '30%', marginBottom: '8px' }}>
                            <button
                                onClick={handleAddClient}
                                className='add-client-btn-user-modal'
                                disabled={isAddButtonDisabled}
                                type="button"
                                style={{
                                    opacity: isAddButtonDisabled ? 0.5 : 1,
                                    cursor: isAddButtonDisabled ? 'not-allowed' : 'pointer',
                                    transition: 'opacity 0.2s'
                                }}

                            >
                                Add Client{selectedDropdownClientIds.size > 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>

                 
                    {assignedClients.length > 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '16px',
                            marginBottom: '4px'
                        }}>
                            <span
                                onClick={handleRemoveAllClick}
                                style={{
                                    fontSize: '12px',
                                    color: '#dc3545',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontWeight: '500'
                                }}
                            >
                                Remove All
                            </span>
                        </div>
                    )}

                    {assignedClients.length > 0 && (
                        <div
                            className='ag-grid-table-structure'
                            style={{
                                height: `${computedGridHeight}px`,
                                width: '100%',
                                position: 'relative',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                marginTop: '0px'
                            }}
                        >
                            <AgGrid
                                rowData={assignedClients}
                                columnDefs={assignedClientsColumnDefs}
                                enableSearch={false}
                                enableSorting={false}
                                enableFilter={false}
                                pagination={false}
                                isClientUser={true}
                                height={`${computedGridHeight}px`}
                                frameworkComponents={{
                                    customTooltip: CustomTooltip  
                                }}
                                hideSelectAll={true}
                                hidePivotMode={true}
                                hideExcelExport={true}
                                hideExportButton={true}
                                hideColumnMenu={true}
                                hideSideBar={true}
                                gridOptions={{
                                    defaultColDef: {
                                        resizable: false,
                                        flex: 1,
                                        minWidth: 100,
                                    },
                                    headerHeight: 40,
                                    rowHeight: 40,
                                    domLayout: 'normal',
                                    suppressHorizontalScroll: false,
                                }}
                                onGridReady={(params) => {
                                    params.api.sizeColumnsToFit();
                                    const header = document.querySelector('.ag-header');
                                    if (header) {
                                        header.style.position = 'sticky';
                                        header.style.top = '0';
                                        header.style.backgroundColor = '#f5f5f5';
                                        header.style.zIndex = '1000';
                                        header.style.borderBottom = '1px solid #ddd';
                                    }
                                    const bodyViewport = document.querySelector('.ag-body-viewport');
                                    if (bodyViewport) {
                                        bodyViewport.style.overflowY = 'auto';
                                        bodyViewport.style.overflowX = 'auto';
                                    }
                                }}
                            />
                        </div>
                    )}

                    {assignedClients.length === 0 && !loadingClientTypes && (!required || !touched || !error) && (
                        <div style={{
                            textAlign: 'start',
                            padding: '20px',
                            color: '#6c757d',
                            fontWeight: '400',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            marginTop: '16px',
                            fontSize: '14px'
                        }}>
                            No Clients Assigned Yet
                        </div>
                    )}
                </div>

             
                {showConfirmModal && clientToRemove && (
                    <>
                        <div className="modal-overlay" />
                        <div
                            className="message-modal-warning"

                        >
                            <div className="message-content-wrapper-remove" >
                                <Icon name="warning-icon" size={32} />
                                <div className="message-title-remove" >
                                    Remove client from user?
                                </div>

                                <span>This client will no longer be assigned to this user.</span>
                            </div>



                            <div className='remove-btn-container' >
                                <button
                                    onClick={handleCancelRemove}

                                    className='discard-changes-btn'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmRemove}
                                    className='remove-btn-client'


                                >
                                    Remove Client
                                </button>
                            </div>
                        </div>
                    </>
                )}

              
                {showRemoveAllConfirm && (
                    <>
                        <div className="modal-overlay" />
                        <div className="message-modal-warning">
                            <div className="message-content-wrapper-remove">
                                <Icon name="warning-icon" size={32} />
                                <div className="message-title-remove">
                                    Remove all clients from user?
                                </div>

                                <span>All assigned clients will be removed from this user.</span>
                            </div>

                            <div className='remove-btn-container'>
                                <button
                                    onClick={handleCancelRemoveAll}
                                    className='discard-changes-btn'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmRemoveAll}
                                    className='remove-btn-client'
                                >
                                    Remove All
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };


    const UserTypeSelect = ({ field, formData, onChange, error, touched }) => {
        const handleUserTypeChange = (e) => {
            const selectedValue = e.target.value;

            if (onChange) {
                onChange(e);
            }

            const isEmployee = (selectedValue || '').toLowerCase() === 'employee';

            if (isEmployee) {
            
                if (onChange) {
                    onChange({
                        target: {
                            name: 'adminAccess',
                            type: 'checkbox',
                            checked: true
                        }
                    });
                }

                const allClients = managedClientTypes.map((type) => {
                    const clientId = type.clientTypeID?.toString() || type.id || type.value;
                    const clientName = type.clientTypeName || type.label || `Client Type ${type.clientTypeID}`;
                    return {
                        clientName,
                        clientID: clientId,
                        id: clientId,
                        clientTypeID: type.clientTypeID,
                        clientTypeName: clientName
                    };
                });
                setAssignedClients(allClients);
            } else {
               
                if (onChange) {
                    onChange({
                        target: {
                            name: 'adminAccess',
                            type: 'checkbox',
                            checked: false
                        }
                    });
                }
              
                setAssignedClients([]);
            }

        };

        return (
            <div style={{ position: 'relative' }}>
                <label className="form-label">
                    User Type<span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                </label>

                <select
                    name="userType"
                    value={formData['userType'] || ''}
                    onChange={handleUserTypeChange}
                    required
                    disabled={loadingUserTypes}
                    className={`user-type-dropdown ${error && touched ? 'input-error' : ''}`}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${error && touched ? '#dc3545' : '#ced4da'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        appearance: 'none', 
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">
                        {loadingUserTypes ? 'Loading user types...' : 'Please select'}
                    </option>


                    {userTypes.length > 0 ? (
                        userTypes.map((type) => {
                            const value = type.userTypeName || type.value || type.id;
                            const displayName = type.userTypeName || type.value || type.id;

                            return (
                                <option key={value} value={value}>
                                    {displayName}
                                </option>
                            );
                        })
                    ) : (
                        <>
                            <option value="employee">Employee</option>
                            <option value="admin">Admin</option>
                            <option value="cager">Cager</option>
                            <option value="vendor">Vendor</option>
                            <option value="consultant">Consultant</option>
                            <option value="client">Client</option>
                        </>
                    )}
                </select>

            
                <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: 'calc(50% + 4px)', 
                    pointerEvents: 'none',
                    color: '#6c757d'
                }}>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M4 6L8 10L12 6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        );
    };

    const PasswordFieldComponent = ({ field, formData, onChange, error, touched }) => {
        const handlePaste = (e) => {
            e.preventDefault();
            return false;
        };

        const handleCopy = (e) => {
            e.preventDefault();
            return false;
        };

        return (
            <div>
                <label className="form-label">
                    {field.label}
                    {field.required && <span className="required-asterisk">*</span>}
                </label>
                <div style={{ position: 'relative' }}>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={onChange}
                        onPaste={handlePaste}
                        onCopy={handleCopy}
                        placeholder={field.placeholder}
                        required={field.required}
                        className={`password-input-field ${error && touched ? 'input-error' : ''}`}
                        maxLength={20}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${error && touched ? '#dc3545' : '#ced4da'}`,
                            borderRadius: '4px',
                            fontSize: '14px',
                            paddingRight: '35px'
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className='password-eye-icon'
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        tabIndex="-1"
                    >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>
            </div>
        );
    };

  
    const modalFields = useMemo(() => {
        return [
            [
                {
                    name: 'firstName',
                    label: 'First Name',
                    type: 'text',
                    required: true,
                    placeholder: 'Enter first name',
                    className: 'form-group-half',
                    maxLength: 16,
                    validate: (value) => {
                        if (!value || value.trim() === '') return 'First Name is required';
                        if (value.length > 16) return 'First Name cannot exceed 16 characters';

                        const regex = /^[a-zA-Z\s\-']+$/;

                        if (!regex.test(value)) {
                            return 'First Name can contain only letters';
                        }

                        if (/[0-9]/.test(value)) return 'First Name cannot contain numbers';
                        if (/[!@#$%^&*()_+=\[\]{};:"\\|,.<>\/?~`]+/.test(value)) {
                            return 'First Name cannot contain special characters';
                        }

                        return '';
                    }
                },
                {
                    name: 'lastName',
                    label: 'Last Name',
                    type: 'text',
                    required: true,
                    placeholder: 'Enter last name',
                    className: 'form-group-half',
                    maxLength: 16,
                    validate: (value) => {
                        if (!value || value.trim() === '') return 'Last Name is required';
                        if (value.length > 16) return 'Last Name cannot exceed 16 characters';

                        const regex = /^[a-zA-Z\s\-']+$/;

                        if (!regex.test(value)) {
                            return 'Last Name can contain only letters';
                        }

                        if (/[0-9]/.test(value)) return 'Last Name cannot contain numbers';
                        if (/[!@#$%^&*()_+=\[\]{};:"\\|,.<>\/?~`]+/.test(value)) {
                            return 'Last Name cannot contain special characters';
                        }
                        return '';
                    }
                }
            ],
            {
                name: 'email',
                label: 'Email',
                type: 'email',
                required: true,
                placeholder: 'Enter email address',
                maxLength: 64,
                validate: (value) => {
                    if (!value || value.trim() === '') return 'Email is required';
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
                    if (value.length > 64) return 'Email cannot exceed 64 characters';
                    return '';
                }
            },
            [
                {
                    type: 'custom',
                    name: 'password',
                    label: 'Password',
                    component: PasswordFieldComponent,
                    required: modalMode === 'add',
                    placeholder: modalMode === 'add' ? 'Enter password' : 'Leave blank to keep current',
                    className: 'form-group-half',
                    minLength: 8,
                    maxLength: 20,
                    complexity: {
                        minLength: 8,
                        maxLength: 20,
                        requireUppercase: true,
                        requireLowercase: true,
                        requireNumbers: true,
                        requireSpecialChars: true,
                        specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?'
                    },
                    validate: (value) => {
                        if (modalMode === 'add') {
                            if (!value || value.trim() === '') {
                                return 'Password is required';
                            }

                            if (value.length < 8) return 'Password must be at least 8 characters';
                            if (value.length > 20) return 'Password cannot exceed 20 characters';

                            const hasUpperCase = /[A-Z]/.test(value);
                            const hasLowerCase = /[a-z]/.test(value);
                            const hasNumber = /\d/.test(value);
                            const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

                            if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
                                const missing = [];
                                if (!hasUpperCase) missing.push('uppercase letter');
                                if (!hasLowerCase) missing.push('lowercase letter');
                                if (!hasNumber) missing.push('number');
                                if (!hasSpecialChar) missing.push('special character');

                                return `Password must contain at least ${missing.join(', ')}`;
                            }
                        } else if (modalMode === 'edit') {
                            if (value && value.trim() !== '') {
                                if (value.length < 8) return 'Password must be at least 8 characters';
                                if (value.length > 20) return 'Password cannot exceed 20 characters';

                                const hasUpperCase = /[A-Z]/.test(value);
                                const hasLowerCase = /[a-z]/.test(value);
                                const hasNumber = /\d/.test(value);
                                const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

                                if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
                                    const missing = [];
                                    if (!hasUpperCase) missing.push('uppercase letter');
                                    if (!hasLowerCase) missing.push('lowercase letter');
                                    if (!hasNumber) missing.push('number');
                                    if (!hasSpecialChar) missing.push('special character');

                                    return `Password must contain at least ${missing.join(', ')}`;
                                }
                            }
                        }
                        return '';
                    }
                },
                {
                    name: 'username',
                    label: 'Username',
                    type: 'text',
                    required: true,
                    placeholder: 'Enter username',
                    className: 'form-group-half',
                    maxLength: 16,
                    disabled: modalMode === 'edit',
                    readOnly: modalMode === 'edit',
                    validate: (value) => {
                        if (!value || value.trim() === '') return 'Username is required';
                        if (value.length < 3) return 'Username must be at least 3 characters';
                        if (value.length > 16) return 'Username cannot exceed 16 characters';
                        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?\s]/.test(value)) {
                            return 'Username cannot contain special characters or spaces';
                        }
                        if (!/^[a-zA-Z0-9._]+$/.test(value)) {
                            return 'Username can only contain letters, numbers, dots, and underscores';
                        }
                        return '';
                    }
                }
            ],
            [
                {
                    type: 'custom',
                    name: 'userType',
                    component: UserTypeSelect,
                    className: 'form-group-half',
                    required: true,
                    validate: (value) => {
                        if (!value || value.trim() === '') return 'User Type is required';
                        return '';
                    }
                },
                {
                    type: 'custom',
                    name: 'toggleSection',
                    component: ToggleSwitch,
                    className: 'form-group-half'
                }
            ],
            {
                type: 'custom',
                name: 'managedClients',
                component: ManagedClientsWithAddButton,
                required: true,
                required: modalMode === 'add',
              
                validate: (value, formData) => {
                    
                    if (assignedClients.length === 0) {
                        return 'At least one client is required.';
                    }
                    return '';
                }

            }
        ];
    }, [showPassword, modalMode, formData, assignedClients, userTypes, loadingUserTypes, managedClientTypes, loadingClientTypes]);

  
    const handleModalSave = async (formData) => {


        try {
            setIsSubmitting(true);

          
            const managedClientIds = assignedClients.map(client => {
                const id = client.clientTypeID || client.id || client.clientID;
                if (id) {
                    const intId = parseInt(id, 10);
                    return isNaN(intId) ? null : intId;
                }
                return null;
            }).filter(id => id !== null && !isNaN(id));

           
            const managedClientNames = assignedClients.map(client =>
                client.clientTypeName || client.clientName || ''
            ).filter(name => name !== '');

            
            const userTypeName = mapUserTypeToApi(formData.userType);

           
            const selectedUserTypeObj = userTypes.find(type =>
                type.userTypeName === formData.userType ||
                type.userTypeName?.toLowerCase() === formData.userType?.toLowerCase() ||
                type.id === formData.userType ||
                type.value === formData.userType
            );

            const userTypeID = selectedUserTypeObj
                ? (selectedUserTypeObj.userTypeID || selectedUserTypeObj.id || selectedUserTypeObj.value)
                : getUserTypeIdFromName(userTypeName); 
           
            const userData = {
                userName: formData.username,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password || '',
                userTypeID: userTypeID,
                isActive: formData.active,
                isAdmin: formData.adminAccess,
                managedClientIds: managedClientIds,
                userTypeName: userTypeName
            };

          
            let response;
            if (modalMode === 'add') {
                response = await CreateUser(userData);
              
                await fetchUsers();
              


                setSuccessText(
                    `User "${formData.firstName} ${formData.lastName}" created successfully.`
                );
                setShowSuccessMessage(true);


            } else {
                const userId = parseInt(editingUserId, 10);
                if (isNaN(userId)) {
                    throw new Error('Invalid user ID for update');
                }

                await UpdateUserById(userId, userData);

                const apiUserType = mapUserTypeToApi(formData.userType);

                setRowData(prevData =>
                    prevData.map(user => {
                        const isEditingUser = user.id === editingUserId ||
                            (user.originalData && user.originalData.userID === editingUserId) ||
                            user.id === userId.toString();

                        if (isEditingUser) {
                            return {
                                ...user,
                                fullName: `${formData.firstName} ${formData.lastName}`,
                                userName: formData.username,
                                userType: apiUserType,
                                isActive: formData.active,
                                originalData: {
                                    ...user.originalData,
                                    ...userData,
                                    userTypeName: apiUserType,
                                    userTypeID: userTypeID
                                },
                                managedClientIds: managedClientIds,
                                managedClientNames: managedClientNames
                            };
                        }
                        return user;
                    })
                );

                setSuccessText(`User "${formData.firstName} ${formData.lastName}" updated successfully.`);
                setShowSuccessMessage(true);
            }

           
            resetForm();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error saving user:', error);

            let errorMessage = `Failed to ${modalMode === 'add' ? 'create' : 'update'} user.`;

            if (error.response && error.response.data) {
                const errorData = error.response.data;

               

                if (errorData.errors && errorData.errors.Email) {
                    errorMessage = `Email already exists: "${formData.email}"`;
                }
                else if (errorData.errors && errorData.errors.UserName) {
                    errorMessage = `Username already exists: "${formData.username}"`;
                }
                else if (errorData.message && errorData.message.includes("Email already exists")) {
                    errorMessage = `Email already exists: "${formData.email}"`;
                }
                else if (errorData.message && errorData.message.includes("Username already exists") ||
                    errorData.message && errorData.message.includes("UserName already exists") ||
                    errorData.message && errorData.message.includes("user name already exists")) {
                    errorMessage = `Username already exists: "${formData.username}"`;
                }
                else if (errorData.message && errorData.message.includes("AppValidationErrors")) {
                    if (errorData.message.includes("Email") || errorData.message.includes("email")) {
                        errorMessage = `Email already exists: "${formData.email}"`;
                    } else if (errorData.message.includes("Username") || errorData.message.includes("UserName") || errorData.message.includes("user name")) {
                        errorMessage = `Username already exists: "${formData.username}"`;
                    } else {
                        errorMessage = "Validation error. Please check your input.";
                    }
                }
                else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            }
            else if (error.message) {
                if (error.message.includes("Email already exists")) {
                    errorMessage = `Email already exists: "${formData.email}"`;
                } else if (error.message.includes("Username already exists") ||
                    error.message.includes("UserName already exists") ||
                    error.message.includes("user name already exists")) {
                    errorMessage = `Username already exists: "${formData.username}"`;
                } else {
                    errorMessage = error.message;
                }
            }

            alert(errorMessage);
            setIsSubmitting(false);
            return;
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setShowPassword(false);
        resetForm();
    };

  
    const resetForm = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            username: '',
            userType: '',
            active: true,
            adminAccess: false,
            managedClients: ''
        });
        setAssignedClients([]);
        setShowPassword(false);
        setModalMode('add');
        setEditingUserId(null);
        setIsSubmitting(false);
        setEditLoading(false);
    };

    // Handle Add User button click
    const handleAddUser = () => {
        resetForm();
        setModalMode('add');
        setIsModalOpen(true);
    };

    // Handle grid ready
    const onGridReady = (params) => {
        gridRef.current = params;
    };

    const columnDefs = [
        {
            field: 'id',
            headerName: 'ID',
            sortable: true,
            filter: 'agTextColumnFilter',
            width: 120,
            maxWidth: 200,
            minWidth: 100,
            suppressSizeToFit: true,
            valueGetter: (params) => {
                if (!params || !params.data) return '';
                return params.data.originalData?.userID || params.data.id || '';
            }
        },
        {
            field: 'fullName',
            headerName: 'Full Name',
            sortable: true,
            filter: 'agTextColumnFilter',
            minWidth: 180,
            flex: 1.5,
            valueGetter: (params) => {
                if (!params || !params.data) return '';
                if (params.node && params.node.rowGroupIndex !== undefined) {
                    return params.node.key || '';
                }
                const value = params.data.fullName || '';
                return toPascalCase(value);
            },
            valueFormatter: (params) => {
                if (!params || params.value === null || params.value === undefined) return '';
                return params.value;
            }
        },
        {
            field: 'userName',
            headerName: 'Username',
            sortable: true,
            filter: 'agTextColumnFilter',
            minWidth: 150,
            flex: 1,
            valueGetter: (params) => {
                if (!params || !params.data) return '';
                if (params.node && params.node.rowGroupIndex !== undefined) {
                    return params.node.key || '';
                }
                const value = params.data.userName || '';
                return toPascalCase(value);
            },
            valueFormatter: (params) => {
                if (!params || params.value === null || params.value === undefined) return '';
                return params.value;
            }
        },
        {
            field: 'userType',
            headerName: 'User Type',
            sortable: true,
            minWidth: 150,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            filterParams: {
                values: ['Admin', 'Cager', 'Client', 'Consultant', 'Employee', 'List Agency', 'Vendor'],
                applyButton: true,
                clearButton: true,
                closeOnApply: true,
                suppressSorting: true
            },
            valueGetter: (params) => {
                if (!params || !params.data) return '';
                if (params.node && params.node.rowGroupIndex !== undefined) {
                    return params.node.key || '';
                }
                const value = params.data.userType || '';
                if (!value) return '';

                const userTypeMap = {
                    'admin': 'Admin',
                    'cager': 'Cager',
                    'client': 'Client',
                    'consultant': 'Consultant',
                    'employee': 'Employee',
                    'list agency': 'List Agency',
                    'listagency': 'List Agency',
                    'list_agency': 'List Agency',
                    'vendor': 'Vendor'
                };

                const lowerValue = value.toLowerCase().trim();
                return userTypeMap[lowerValue] || toPascalCase(value);
            },
            cellRenderer: (params) => {
                if (!params || params.value === null || params.value === undefined) return '';
                return <span>{params.value}</span>;
            }
        },
        {
            headerName: 'Status',
            field: 'isActive',
            sortable: true,
            width: 180,
            maxWidth: 180,
            minWidth: 180,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                const isActive = params.value;
                return (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 0'
                    }}>
                        <Icon
                            name={isActive ? 'active' : 'in-active'}
                            size={16}
                            alt="status"
                        />
                        <span style={{
                            color: isActive ? '#28a745' : '#dc3545',
                            fontWeight: '500',
                            fontSize: '13px'
                        }}>
                            {isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                );
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            filter: false,
            cellRenderer: EditIconRenderer,
            sortable: false,
            width: 100,
            minWidth: 80,
            maxWidth: 120,
            suppressSizeToFit: true,
            suppressAutoSize: true,
            cellRendererParams: {
                suppressGroupRows: true
            }
        }
    ];

    // Render loading state
    if (loading && rowData.length === 0) {
        return (
            <AisScreenLoader />
        );
    }

    return (
        <div className="user-maintenance" style={{ marginBottom: "50px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4 position-absolute gap-3 ">
                <button
                    onClick={handleAddUser}
                    className='add-user-button'
                    disabled={isSubmitting || editLoading}

                >
                    <span className='add-icon'>+</span>
                    {isSubmitting ? 'Add User' : ' Add User'}
                </button>

                <div style={{ flex: 1 }}></div>
            </div>

            {error && (
                <div className='fetch-users-error-message' style={{
                    padding: '12px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {error}
                    <button
                        onClick={fetchUsers}
                        className='fetch-users-retry'
                        style={{
                            padding: '4px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            <div style={{ maxHeight: "500px" }}>
                <AgGrid
                    enableSearch={true}
                    searchPlaceholder="Search user..."
                    enableSorting={true}
                    enableFilter={true}
                    pagination={true}
                    height="500px"
                    quickFilterText={searchText}
                    hideExportButton={true}
                    hideSideBar={true}
                    hideSelectAll={true}
                    onQuickFilterChange={setSearchText}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                    onRowClicked={(params) => {
                        console.log('Row clicked:', params.data);
                    }}
                />

                {rowData.length === 0 && !loading && !error && (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#666',
                        fontStyle: 'italic'
                    }}>
                        No users found. Click "Add User" to create a new user.
                    </div>
                )}
            </div>

            <CommonModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSave={handleModalSave}
                title={modalMode === 'add' ? 'Create User' : 'Edit User'}
                fields={modalFields}
                initialData={formData}
                submitText={modalMode === 'add' ? 'Create User' : 'Update User'}
                cancelText="Cancel"
                width="500px"
                validateOnSubmit={true}
            />
            {showSuccessMessage && (
                <>
                    <div className="modal-overlay" />
                    <SuccessMessage
                        title="Success"
                        message={successText}
                        onClose={() => setShowSuccessMessage(false)}
                    />
                </>
            )}
        </div>
    );
};

export default UserMaintenance;
